"use client"

import { motion } from "framer-motion"
import {
  Watch,
  Newspaper,
  Instagram,
  DollarSign,
  FileText,
  Brain,
  BarChart3,
  type LucideIcon,
} from "lucide-react"

type Integration = {
  icon: LucideIcon
  label: string
  color: string
}

const integrations: Integration[] = [
  { icon: Brain, label: "Motivation", color: "from-purple-500 to-violet-600" },
  { icon: BarChart3, label: "Statistics", color: "from-teal-500 to-cyan-600" },
  { icon: Watch, label: "Wearables", color: "from-blue-500 to-blue-600" },
  { icon: FileText, label: "Contracts", color: "from-orange-500 to-red-600" },
  { icon: Newspaper, label: "News", color: "from-red-500 to-orange-600" },
  { icon: DollarSign, label: "Financial", color: "from-green-500 to-emerald-600" },
  { icon: Instagram, label: "Socials", color: "from-pink-500 to-purple-600" },
]

type DataIntegrationsHubProps = {
  compact?: boolean
}

export function DataIntegrationsHub({ compact = false }: DataIntegrationsHubProps) {
  const radius = compact ? 190 : 250
  const containerHeight = compact ? "h-[420px] md:h-[480px]" : "h-[500px] md:h-[600px]"
  const hubSize = compact ? "h-32 w-32" : "h-40 w-40"
  const glowSize = compact ? "h-32 w-32" : "h-40 w-40"
  const cardWidth = compact ? 140 : 160
  const cardOffset = compact ? 70 : 80
  const cardEdgeRadius = compact ? 38 : 45
  const iconBox = compact ? "h-10 w-10" : "h-12 w-12"
  const iconSize = compact ? "h-5 w-5" : "h-6 w-6"

  return (
    <div className="relative">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="absolute inset-0 z-10 flex items-center justify-center"
      >
        <div className="relative">
          <motion.div
            className={`absolute inset-0 ${glowSize} rounded-full bg-gradient-to-r from-blue-500 to-purple-500 opacity-20 blur-xl`}
            animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <div
            className={`relative ${hubSize} flex items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-purple-600 shadow-2xl`}
          >
            <span className="select-none text-lg font-bold tracking-tight text-white md:text-xl">
              Sigm<span className="bg-gradient-to-r from-blue-200 to-purple-200 bg-clip-text text-transparent">AI</span>
            </span>
          </div>
        </div>
      </motion.div>

      <div className={`relative ${containerHeight}`}>
        {integrations.map((integration, index) => {
          const angleInDegrees = index * (360 / integrations.length) - 90
          const angleInRadians = angleInDegrees * (Math.PI / 180)
          const x = Math.cos(angleInRadians) * radius
          const y = Math.sin(angleInRadians) * radius

          return (
            <motion.div
              key={integration.label}
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.6,
                delay: 0.4 + index * 0.1,
                type: "spring",
                stiffness: 100,
              }}
              className="absolute"
              style={{
                left: `calc(50% + ${x}px - ${cardOffset}px)`,
                top: `calc(50% + ${y}px - ${cardOffset / 2}px)`,
              }}
            >
              {(() => {
                const cardHeight = compact ? 72 : 80
                const cardCenterX = cardWidth / 2
                const cardCenterY = cardHeight / 2
                const directionX = -x
                const directionY = -y
                const distance = Math.sqrt(directionX * directionX + directionY * directionY)
                const normalizedDirX = directionX / distance
                const normalizedDirY = directionY / distance
                const startX = cardCenterX + normalizedDirX * cardEdgeRadius
                const startY = cardCenterY + normalizedDirY * cardEdgeRadius
                const endX = cardCenterX + directionX
                const endY = cardCenterY + directionY
                const lineLength = Math.sqrt((endX - startX) ** 2 + (endY - startY) ** 2)
                const lineAngle = Math.atan2(endY - startY, endX - startX) * (180 / Math.PI)

                return (
                  <motion.div
                    className="absolute z-0"
                    style={{
                      left: `${startX}px`,
                      top: `${startY}px`,
                      width: `${lineLength}px`,
                      height: "3px",
                      transformOrigin: "0 50%",
                      transform: `rotate(${lineAngle}deg)`,
                    }}
                  >
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-blue-600 shadow-lg"
                      initial={{ scaleX: 0, opacity: 0 }}
                      whileInView={{ scaleX: 1, opacity: 0.8 }}
                      viewport={{ once: true }}
                      transition={{
                        duration: 1.5,
                        delay: 1.2 + index * 0.15,
                        ease: "easeInOut",
                      }}
                      style={{ transformOrigin: "0 50%" }}
                    />
                    <motion.div
                      className="absolute h-3 w-3 rounded-full border-2 border-white bg-blue-400 shadow-lg"
                      style={{ top: "-3px", left: "0px" }}
                      initial={{ x: 0, opacity: 0 }}
                      animate={{ x: lineLength - 6, opacity: [0, 1, 1, 0] }}
                      transition={{
                        duration: 1.5,
                        delay: 1.2 + index * 0.15,
                        ease: "easeInOut",
                        repeat: Infinity,
                        repeatDelay: 2,
                      }}
                    />
                  </motion.div>
                )
              })()}

              <div
                className={`relative z-10 rounded-xl border border-gray-200 bg-white p-3 text-center shadow-lg transition-shadow duration-300 hover:shadow-xl ${compact ? "w-[140px]" : "w-40"}`}
              >
                <div
                  className={`mx-auto mb-2 flex ${iconBox} items-center justify-center rounded-lg bg-gradient-to-r ${integration.color}`}
                >
                  <integration.icon className={`${iconSize} text-white`} />
                </div>
                <h3 className="text-sm text-gray-900">{integration.label}</h3>
              </div>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}
